import {
  EnvironmentProviders,
  makeEnvironmentProviders,
  inject,
  APP_INITIALIZER,
  Injectable,
} from '@angular/core';
import {
  provideTransloco,
  TranslocoService,
  TranslocoModule,
  TranslocoLoader,
  Translation,
} from '@jsverse/transloco';
import { HttpClient } from '@angular/common/http';
import { ConfigService } from '@platform/core';
import { Observable, firstValueFrom } from 'rxjs';
import { PrimeNG } from 'primeng/config';

/**
 * HTTP Loader for Transloco
 * Loads translation files from configured path
 * Based on: https://jsverse.gitbook.io/transloco/getting-started/installation
 */
@Injectable({ providedIn: 'root' })
export class PlatformTranslocoLoader implements TranslocoLoader {
  private readonly http = inject(HttpClient);
  private readonly configService = inject(ConfigService);

  getTranslation(lang: string): Observable<Translation> {
    const i18nConfig = this.configService.getI18n();
    const loadPath = i18nConfig.loadPath || '/assets/i18n/{{lang}}.json';

    // Replace placeholders with actual locale
    let path = loadPath.replace('{{lang}}', lang).replace('{{locale}}', lang);

    // Use relative path for better deployment compatibility
    // If path doesn't start with /, it's relative to base href
    if (!path.startsWith('/') && !path.startsWith('./') && !path.startsWith('../')) {
      path = './' + path;
    }

    // Remove any double slashes except after protocol
    path = path.replace(/([^:]\/)\/+/g, '$1');

    return this.http.get<Translation>(path);
  }
}

/**
 * Initialize i18n with platform configuration
 */
function initializeI18n() {
  return async () => {
    const configService = inject(ConfigService);
    const transloco = inject(TranslocoService);
    const i18nConfig = configService.getI18n();
    const primeng = inject(PrimeNG);

    // Get default locale from config or localStorage
    let defaultLocale = i18nConfig.defaultLocale || 'en';

    // Check if locale is persisted
    if (typeof localStorage !== 'undefined') {
      const persistedLocale = localStorage.getItem('platform.locale');
      if (persistedLocale && i18nConfig.supportedLocales?.includes(persistedLocale)) {
        defaultLocale = persistedLocale;
      }
    }

    // Set available languages
    const supportedLocales = i18nConfig.supportedLocales || [defaultLocale];
    transloco.setAvailableLangs(supportedLocales);

    // Load and set active language
    try {
      await firstValueFrom(transloco.load(defaultLocale));
      transloco.setActiveLang(defaultLocale);
      primeng.setTranslation(transloco.getTranslation('primeng'));
      // Update HTML lang attribute and direction
      if (typeof document !== 'undefined') {
        const locale = transloco.getActiveLang();
        document.documentElement.lang = locale;

        // Update direction for RTL languages
        const rtlLanguages = i18nConfig.rtlLanguages || ['fa', 'ar', 'he', 'ur'];
        const isRtl = rtlLanguages.includes(locale);
        document.documentElement.dir = isRtl ? 'rtl' : 'ltr';
        document.body.dir = isRtl ? 'rtl' : 'ltr';
        isRtl
          ? document.documentElement.classList.add('app-rtl')
          : document.documentElement.classList.remove('app-rtl');
      }
    } catch (error: any) {
      // AbortError is expected when navigation/reload cancels in-flight requests.
      if (error?.name !== 'AbortError') {
        console.error('Failed to load translations:', error);
      }
      // Still set the language even if loading fails or is aborted
      transloco.setActiveLang(defaultLocale);
    }
  };
}

/**
 * Provides i18n configuration for the platform
 * Integrates with platform configuration system
 * Based on: https://jsverse.gitbook.io/transloco/getting-started/installation
 * 
 * @returns EnvironmentProviders for i18n
 * 
 * @example
 * ```typescript
 * export const appConfig: ApplicationConfig = {
 *   providers: [
 *     providePlatformConfig(environment),
 *     providePlatformI18n(),
 *     // ... other providers
 *   ],
 * };
 * ```
 */
export function providePlatformI18n(): EnvironmentProviders {
  return makeEnvironmentProviders([
    // Provide Transloco with configuration
    // Config values will be set during initialization via APP_INITIALIZER
    provideTransloco({
      config: {
        availableLangs: ['en'], // Will be updated from config during init
        defaultLang: 'en', // Will be updated from config during init
        fallbackLang: 'en',
        // Remove this option if your application doesn't support changing language in runtime
        reRenderOnLangChange: true,
        missingHandler: {
          useFallbackTranslation: true,
          logMissingKey: true,
        },
      },
      loader: PlatformTranslocoLoader,
    }),

    // Initialize i18n with platform config
    // This runs before the app starts, ensuring translations are loaded
    {
      provide: APP_INITIALIZER,
      useFactory: initializeI18n,
      multi: true,
      deps: [ConfigService, TranslocoService],
    },
  ]);
}

// Re-export TranslocoModule for convenience
export { TranslocoModule };
